import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'md' | 'lg' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 shadow-soft',
  secondary: 'bg-brand-light text-brand-700 hover:bg-brand-100',
  outline: 'bg-transparent border border-ink-200 text-ink-700 hover:bg-ink-50',
  ghost: 'bg-transparent text-ink-600 hover:bg-ink-50',
  danger: 'bg-critical text-white hover:brightness-95',
};

const sizeClasses: Record<Size, string> = {
  md: 'h-12 px-4 text-[15px] rounded-xl',
  lg: 'h-[52px] px-5 text-base rounded-2xl',
  icon: 'h-12 w-12 rounded-xl justify-center',
};

/**
 * Boton base del Design System de LCC.
 * Altura minima 48-52px para cumplir el estandar mobile-first (>= 50px tocable).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'lg', leftIcon, rightIcon, isLoading, fullWidth, className = '', children, disabled, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={[
        'inline-flex items-center justify-center gap-2 font-semibold transition-all duration-200 select-none',
        'disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {isLoading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        leftIcon
      )}
      {children}
      {rightIcon}
    </button>
  );
});
